import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import {
  ApiResponse_GetAll_Building,
  useGetAllBuildings,
} from '@/api/endpoints/building/useGetAllBuildings';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { getResolvedRoutePath } from '@/utils/router';

import BuildingCreateModal from '@/components/building/BuildingCreateModal';

const TABLE_HEAD = [
  { id: 'name', label: 'Building Name', sortable: true, width: '25%' },
  { id: 'address_text', label: 'Address', sortable: true, width: '50%' },
  { id: '_count.units', label: 'Units', sortable: true, width: '15%' },
  { id: 'units._count.tenants', label: 'Tenants', sortable: true, width: '10%' },
];

type Order = 'asc' | 'desc';
type Building = ApiResponse_GetAll_Building[0];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'name' || orderBy === 'address_text') {
    const aValue = String(a[orderBy]).toLowerCase();
    const bValue = String(b[orderBy]).toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

const PropertyLocationViewBuildingListSubpage: React.FC = () => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Building>('name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isBuildingCreateModalOpen, setIsBuildingCreateModalOpen] = useState(false);
  const { id } = useParams<ExtractParams<PMPRoutes.propertyLocationManagerList>>();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openAddBuilding') === 'true') {
      setIsBuildingCreateModalOpen(true);
    }
  }, [location]);

  const getAllBuildingsQuery = useGetAllBuildings({ location_id: Number(id) });

  const handleRequestSort = (property: keyof Building) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllBuildingsQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        item =>
          item.name?.toLowerCase().includes(filterName.toLowerCase()) ||
          item.address_text.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllBuildingsQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onAddBuildingClick = useCallback(() => {
    setIsBuildingCreateModalOpen(true);
  }, []);

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60%' }}>
          Manage buildings within this property location. Add new buildings, view their details, and
          track unit and tenant information.
        </Typography>
        <Button variant="contained" startIcon={<PlusIcon />} onClick={onAddBuildingClick}>
          Add Building
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme => theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search buildings..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === '' ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme => theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof Building)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllBuildingsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllBuildingsQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No buildings found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllBuildingsQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => {
                  const buildingId = String(row.id);
                  const linkPath = getResolvedRoutePath(PMPRoutes.buildingView, { id: buildingId });

                  return (
                    <TableRow
                      key={buildingId}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme => `${theme.palette.secondary[200]} !important`,
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        },
                      }}
                    >
                      <TableCell sx={{ width: '25%' }}>
                        <Link
                          href={linkPath}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': {
                              color: theme => theme.palette.primary.main,
                            },
                          }}
                        >
                          {row.name || 'Unnamed'}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ width: '50%' }}>
                        <Box
                          sx={{
                            lineHeight: 1.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {row.address_text || 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>{row._count?.units ?? 0}</TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        {row.units.reduce((sum, unit) => sum + unit._count.tenants, 0) ?? 0}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isBuildingCreateModalOpen && (
        <BuildingCreateModal
          propertyLocationId={id ? Number(id) : undefined}
          onClose={() => setIsBuildingCreateModalOpen(false)}
        />
      )}
    </Stack>
  );
};

export default PropertyLocationViewBuildingListSubpage;
